import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { DayStrip } from "@/components/DayStrip";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { ClassPicker } from "@/components/ClassPicker";
import { PhotoUpload } from "@/components/PhotoUpload";
import { AuthImage } from "@/components/AuthImage";
import { UpdateReplies } from "@/components/UpdateReplies";
import { ApprovalBadge, RejectReason } from "@/components/ApprovalBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ClipboardList, Utensils, Moon, Star, StickyNote, Trash2 } from "lucide-react";

const CATS = { food: { label: "Food", icon: Utensils, cls: "bg-orange-50 text-orange-600" }, nap: { label: "Nap", icon: Moon, cls: "bg-blue-50 text-blue-600" },
  activity: { label: "Activity", icon: Star, cls: "bg-amber-50 text-amber-600" }, note: { label: "Note", icon: StickyNote, cls: "bg-violet-50 text-violet-600" } };

function NewUpdateDialog({ open, onClose, scope, onSaved }) {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [f, setF] = useState({ category: "activity", title: "", body: "", photo_url: "", student_id: "all" });
  useEffect(() => {
    if (open && scope.class_name) api.get("/students", { params: { branch_id: scope.branch_id, class_name: scope.class_name, section: scope.section } }).then((r) => setStudents(r.data)).catch(() => {});
  }, [open, scope]);
  async function save() {
    try {
      await api.post("/daily-updates", { ...scope, ...f, student_id: f.student_id === "all" ? null : f.student_id });
      toast.success(user?.role === "teacher" ? "Sent for approval" : "Update posted"); setF({ category: "activity", title: "", body: "", photo_url: "", student_id: "all" }); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Post daily update • {scope.class_name} {scope.section}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="update-category" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>For</Label>
              <Select value={f.student_id} onValueChange={(v) => setF({ ...f, student_id: v })}>
                <SelectTrigger data-testid="update-student" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Whole class</SelectItem>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Title</Label><Input data-testid="update-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" placeholder="Circle time & colour activity" /></div>
          <div><Label>Details</Label><Textarea data-testid="update-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="mt-1" /></div>
          <PhotoUpload value={f.photo_url} onChange={(v) => setF({ ...f, photo_url: v })} />
          <Button data-testid="update-save" onClick={save} disabled={!f.title} className="w-full btn-brand">Post update</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DailyUpdatesPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const category = params.get("category") || "";
  const isParent = user?.role === "parent";
  const [scope, setScope] = useState({ branch_id: "", class_name: "", section: "A" });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("");
  const shown = day ? rows.filter((u) => u.date === day) : rows;

  async function load() {
    const p = { ...(category ? { category } : {}) };
    if (!isParent) Object.assign(p, { branch_id: scope.branch_id, class_name: scope.class_name || undefined, section: scope.section || undefined });
    const { data } = await api.get("/daily-updates", { params: p });
    setRows(data);
  }
  useEffect(() => { if (isParent || scope.branch_id) load().catch(() => {}); }, [scope, category]); // eslint-disable-line react-hooks/exhaustive-deps

  async function remove(id) { await api.delete(`/daily-updates/${id}`); load(); }

  return (
    <AppShell>
      <PageHeader eyebrow="Daily Updates" title={category === "activity" ? "Activities" : "Daily Updates"} subtitle={isParent ? "Food, nap, activities and notes from school." : "Share the day with parents."}>
        {!isParent && <Button data-testid="new-update-btn" onClick={() => setOpen(true)} disabled={!scope.class_name} className="btn-brand"><Plus size={16} className="mr-1" /> Post update</Button>}
      </PageHeader>
      {!isParent && <div className="mb-5"><ClassPicker value={scope} onChange={setScope} /></div>}
      <div className="mb-5"><DayStrip testid="updates-day" marked={rows.map((u) => u.date)} countLabel="updates"
        preview={(d) => rows.filter((u) => u.date === d).map((u) => ({ id: u.id, title: u.title, sub: `${CATS[u.category]?.label || u.category} • by ${u.posted_by_name}` }))} value={day} onChange={setDay} clearLabel="All dates" /></div>
      {shown.length === 0 ? <Empty testid="updates-empty" icon={ClipboardList} text={day ? "No updates on this day." : "No updates yet."} /> : (
        <div className="space-y-3">
          {shown.map((u) => { const c = CATS[u.category]; return (
            <div key={u.id} data-testid={`update-${u.id}`} className="bg-white rounded-2xl p-4 shadow-sm flex gap-4">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${c.cls}`}><c.icon size={20} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500"><span className="font-semibold text-slate-700">{c.label}</span> • {fmtDate(u.date)} • {u.class_name} {u.section} • by {u.posted_by_name}{!isParent && u.approval && <ApprovalBadge status={u.approval} testid={`update-status-${u.id}`} />}</div>
                <div className="font-display font-bold text-slate-900 mt-1">{u.title}</div>
                {u.body && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{u.body}</p>}
                {!isParent && <RejectReason item={u} />}
                {u.photo_url && <AuthImage src={u.photo_url} alt="" className="mt-3 h-48 w-full max-w-md rounded-xl object-cover" />}
                <UpdateReplies update={u} />
              </div>
              {!isParent && <button data-testid={`delete-update-${u.id}`} onClick={() => remove(u.id)} className="text-slate-400 hover:text-red-600 self-start"><Trash2 size={16} /></button>}
            </div>
          ); })}
        </div>
      )}
      <NewUpdateDialog open={open} onClose={() => setOpen(false)} scope={scope} onSaved={load} />
    </AppShell>
  );
}
